import {
  Popconfirm as AntPopconfirm,
  PopconfirmProps as AntPopconfirmProps,
  Flex,
  theme,
} from 'antd';
import styled from 'styled-components';
import { ReactNode } from 'react';
import type { GlobalToken } from 'antd';

export type PopconfirmPlacement =
  | 'top'
  | 'topLeft'
  | 'topRight'
  | 'bottom'
  | 'bottomLeft'
  | 'bottomRight'
  | 'left'
  | 'leftTop'
  | 'leftBottom'
  | 'right'
  | 'rightTop'
  | 'rightBottom';

export interface PopconfirmProps extends Omit<
  AntPopconfirmProps,
  'title' | 'okText' | 'cancelText'
> {
  /** Title/question to display */
  title: ReactNode;
  /** Description text */
  description?: ReactNode;
  /** Element that triggers the popconfirm */
  children: ReactNode;
  /** Placement of popconfirm */
  placement?: PopconfirmPlacement;
  /** Text for confirm button */
  confirmText?: string;
  /** Text for cancel button */
  cancelText?: string;
  /** Callback when confirmed */
  onConfirm?: () => void | Promise<void>;
  /** Callback when cancelled */
  onCancel?: () => void;
  /** Whether this is a destructive action */
  danger?: boolean;
  /** Whether confirm button is loading */
  loading?: boolean;
  /** Whether popconfirm is disabled */
  disabled?: boolean;
  /** Custom icon */
  icon?: ReactNode;
}

interface StyledProps {
  $token: GlobalToken;
  $danger?: boolean;
}

const StyledPopconfirm = styled(AntPopconfirm)<StyledProps>`
  .ant-popconfirm-inner {
    font-family: ${({ $token }) => $token.fontFamily};
  }

  .ant-popconfirm-title {
    font-weight: ${({ $token }) => $token.fontWeightStrong};
    color: ${({ $token }) => $token.colorText};
  }

  .ant-popconfirm-description {
    color: ${({ $token }) => $token.colorTextSecondary};
    font-size: ${({ $token }) => $token.fontSizeSM}px;
  }

  .ant-popconfirm-buttons {
    .ant-btn-primary {
      background: ${({ $token, $danger }) =>
        $danger ? $token.colorError : $token.colorPrimary};
      border-color: ${({ $token, $danger }) =>
        $danger ? $token.colorError : $token.colorPrimary};

      &:hover {
        background: ${({ $token, $danger }) =>
          $danger ? $token.colorErrorActive : $token.colorPrimaryActive};
        border-color: ${({ $token, $danger }) =>
          $danger ? $token.colorErrorActive : $token.colorPrimaryActive};
      }
    }
  }
`;

interface IconProps {
  $token: GlobalToken;
}

const WarningIcon = ({ $token }: IconProps) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{ color: $token.colorWarning }}
  >
    <path
      d="M8 5.5V8.5M8 11H8.007M2.456 12.8H13.544C14.373 12.8 14.904 11.904 14.504 11.184L8.96 2.576C8.56 1.856 7.44 1.856 7.04 2.576L1.496 11.184C1.096 11.904 1.627 12.8 2.456 12.8Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DangerIcon = ({ $token }: IconProps) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{ color: $token.colorError }}
  >
    <path
      d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M10 6L6 10M6 6L10 10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Popconfirm component based on Libertify Design System
 *
 * A confirmation dialog that appears when clicking an element.
 * Now uses Ant Design theme tokens for consistent styling.
 *
 * @example
 * ```tsx
 * import { Popconfirm, Button } from '@libertify/ui';
 *
 * // Basic usage
 * <Popconfirm
 *   title="Delete this item?"
 *   description="This action cannot be undone."
 *   onConfirm={() => deleteItem()}
 *   danger
 * >
 *   <Button variant="tertiary">Delete</Button>
 * </Popconfirm>
 *
 * // With custom text
 * <Popconfirm
 *   title="Save changes?"
 *   confirmText="Yes, save"
 *   cancelText="No, discard"
 *   onConfirm={handleSave}
 * >
 *   <Button>Save</Button>
 * </Popconfirm>
 * ```
 */
export const Popconfirm = ({
  title,
  description,
  children,
  placement = 'top',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  danger = false,
  loading = false,
  disabled = false,
  icon,
  ...props
}: PopconfirmProps) => {
  const { token } = theme.useToken();
  const defaultIcon = danger ? (
    <DangerIcon $token={token} />
  ) : (
    <WarningIcon $token={token} />
  );

  return (
    <StyledPopconfirm
      $token={token}
      title={title}
      description={description}
      placement={placement}
      okText={confirmText}
      cancelText={cancelText}
      onConfirm={onConfirm}
      onCancel={onCancel}
      okButtonProps={{
        loading,
        danger,
      }}
      icon={
        <Flex
          align="center"
          justify="center"
          style={{ padding: 4, paddingRight: 8 }}
        >
          {icon ?? defaultIcon}
        </Flex>
      }
      disabled={disabled}
      $danger={danger}
      {...props}
    >
      {children}
    </StyledPopconfirm>
  );
};

export default Popconfirm;
